import { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import BlogLayout from '../blog/BlogLayout';
import MetaTags from '../common/MetaTags';

export default function HelpCategoryView({ categorySlug }) {
  const [articles, setArticles] = useState([]);
  const [categoryName, setCategoryName] = useState('');
  const [loading, setLoading] = useState(true);

  // Convert slug to category name (e.g., "getting-started" -> "Getting Started")
  const slugToName = (slug) => {
    return slug
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  useEffect(() => {
    loadCategoryArticles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categorySlug]);

  async function loadCategoryArticles() {
    try {
      setLoading(true);

      // Convert slug back to category name
      const decodedCategoryName = slugToName(categorySlug);
      setCategoryName(decodedCategoryName);

      // Load all published help articles
      const snapshot = await getDocs(collection(db, 'help_articles'));
      const loadedArticles = snapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .filter(article =>
          article.published === true &&
          (article.category || 'General') === decodedCategoryName
        )
        .sort((a, b) => (a.order || 0) - (b.order || 0));

      setArticles(loadedArticles);
      setLoading(false);
    } catch (error) {
      console.error('Error loading category articles:', error);
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <BlogLayout showSidebar={false} whiteBackground={false}>
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <div style={{ fontSize: '14px', color: '#9b9a97' }}>
            Loading articles...
          </div>
        </div>
      </BlogLayout>
    );
  }

  return (
    <>
      <MetaTags
        title={`${categoryName} - Help Center`}
        description={`Browse all help articles in the ${categoryName} category on VBallStat.`}
        type="website"
      />

      <style>{`
        .category-view-wrapper {
          max-width: 1200px;
          margin: 0 auto;
          padding: 60px 40px;
        }

        .category-header {
          margin-bottom: 48px;
        }

        .category-back-link {
          display: inline-flex;
          align-items: center;
          color: var(--primary);
          text-decoration: none;
          font-size: 14px;
          font-weight: 600;
          margin-bottom: 24px;
          transition: all 0.2s;
        }

        .category-back-link:hover {
          color: var(--primary-hover);
          transform: translateX(-4px);
        }

        .category-title {
          font-size: 42px;
          font-weight: 700;
          margin-bottom: 12px;
          color: var(--text);
        }

        .category-count {
          font-size: 16px;
          color: var(--text-secondary);
        }

        .category-articles-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
          gap: 24px;
        }

        .category-article-card {
          display: block;
          padding: 32px;
          background: white;
          border: 1px solid var(--border);
          border-radius: 12px;
          text-decoration: none;
          color: var(--text);
          transition: all 0.3s ease;
        }

        .category-article-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 24px rgba(0, 0, 0, 0.1);
          border-color: var(--primary);
        }

        .category-article-title {
          font-size: 20px;
          font-weight: 700;
          margin-bottom: 12px;
          color: var(--text);
        }

        .category-article-description {
          font-size: 14px;
          color: var(--text-secondary);
          line-height: 1.6;
          margin: 0;
        }

        .category-empty-state {
          text-align: center;
          padding: 80px 20px;
        }

        .category-empty-icon {
          font-size: 64px;
          margin-bottom: 16px;
        }

        .category-empty-title {
          font-size: 20px;
          font-weight: 700;
          color: var(--text);
          margin-bottom: 8px;
        }

        .category-empty-description {
          font-size: 14px;
          color: var(--text-secondary);
        }

        @media (max-width: 768px) {
          .category-view-wrapper {
            padding: 40px 20px;
          }

          .category-title {
            font-size: 32px;
          }

          .category-articles-grid {
            grid-template-columns: 1fr;
          }

          .category-article-card {
            padding: 24px;
          }
        }
      `}</style>

      <BlogLayout showSidebar={false} whiteBackground={false}>
        <div className="category-view-wrapper">
          {/* Header */}
          <div className="category-header">
            <a href="/help" className="category-back-link">
              <span style={{ marginRight: '8px' }}>←</span>
              Back to Help Center
            </a>
            <h1 className="category-title">{categoryName}</h1>
            <p className="category-count">
              {articles.length} {articles.length === 1 ? 'article' : 'articles'}
            </p>
          </div>

          {/* Articles Grid */}
          {articles.length === 0 ? (
            <div className="category-empty-state">
              <div className="category-empty-icon">📚</div>
              <h2 className="category-empty-title">No articles yet</h2>
              <p className="category-empty-description">
                Check back soon for helpful guides in this category!
              </p>
            </div>
          ) : (
            <div className="category-articles-grid">
              {articles.map(article => (
                <a
                  key={article.id}
                  href={`/help/${article.slug}`}
                  className="category-article-card"
                >
                  <h2 className="category-article-title">{article.title}</h2>
                  {article.description && (
                    <p className="category-article-description">
                      {article.description}
                    </p>
                  )}
                </a>
              ))}
            </div>
          )}
        </div>
      </BlogLayout>
    </>
  );
}
